import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BookingForm.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addMonths } from "date-fns";

const API_BASE_URL = 'https://api.shiroormatha.org/api';
// const API_BASE_URL = 'http://localhost:5000/api';
// const API_BASE_URL = 'https://shiroor-pay-backend-seven.vercel.app/api'
const isLocalTesting = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '[::1]' ||
  process.env.NODE_ENV === 'development'
);
const LOCAL_OTP_CODE = '123456';

const BookingForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URL parameters
  const entityId = searchParams.get('entity_id');

  const eventId = searchParams.get('event_id');
  const sevaId = searchParams.get('seva_id');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // OTP / verification state
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [localOtpMessage, setLocalOtpMessage] = useState('');
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(null);
  const [event, setEvent] = useState(null);
  const [seva, setSeva] = useState(null);
  const [onlinePaymentEnabled, setOnlinePaymentEnabled] = useState(null);
  const [amount, setAmount] = useState(100);
  const [sevaDate, setSevaDate] = useState();
  const [blockedDates, setBlockedDates] = useState([]);
  const [NoOfsevas, setNoOfSevas] = useState([]);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');
  const [devoteeId, setDevoteeId] = useState(null);
  const [devoteeLoading, setDevoteeLoading] = useState(false);
  const [prasadaCollectionMethod, setPrasadaCollectionMethod] = useState('');
  
  // Devotee information state
  const [devoteeInfo, setDevoteeInfo] = useState({
    name: '',
    phone: '',
    address: '',
    pincode: '',
    city: '',
    district: '',
    state: ''
  });
  
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [paymentMethod] = useState('ccavenue'); // 'razorpay' or 'ccavenue'

  // Amount is only editable when the seva itself is free (seva_amount === 0);
  // otherwise the fixed seva amount must not be changed by the devotee.
  const amountLocked = Number(seva?.seva_amount) !== 0;

  const formatLocalDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check for existing devotee by phone number
  const checkDevotee = async (phone) => {
    if (!phone || phone.length < 10) return;
    
    setDevoteeLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/devotee/check/${entityId}/${eventId}/${phone}`);
      
  if (response.data.exists) {
        const devoteeData = response.data.devotee;
        
        // Pre-fill form with devotee data
        setDevoteeInfo({
          name: String(devoteeData.name || ''),
          phone: String(devoteeData.phone || ''),
          address: String(devoteeData.address || ''),
          pincode: String(devoteeData.pincode || ''),
          city: String(devoteeData.city || ''),
          district: String(devoteeData.district || ''),
          state: String(devoteeData.state || '')
        });
        
        setDevoteeId(devoteeData.devote_id);
        
        // Auto-fetch pincode details if pincode is pre-filled
        if (devoteeData.pincode) {
          fetchPincodeDetails(devoteeData.pincode);
        }
        
        console.log('Devotee found and form pre-filled');
      } else {
        setDevoteeId(null);
        console.log('New devotee - ready for data entry');
      }
    } catch (error) {
      console.error('Error checking devotee:', error);
      setDevoteeId(null);
    } finally {
      setDevoteeLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const phone = String(devoteeInfo.phone || '').trim();
    if (!/^\d{10}$/.test(phone)) {
      setError('Enter a valid 10-digit phone number before verification');
      return;
    }

    setError('');
    setOtpSending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/send-otp`, { phone });
      if (response.data && response.data.success) {
        setOtpSent(true);
        setOtp('');
        setLocalOtpMessage('');
        if (response.data.bypass) {
          setLocalOtpMessage(`Local OTP bypass enabled. Use code ${response.data.otp || LOCAL_OTP_CODE} or click Skip OTP.`);
        }
        setError('OTP sent to your phone.');
      } else {
        throw new Error(response.data?.error || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('OTP send failed:', err);
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
      setOtpSent(false);
    } finally {
      setOtpSending(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (!otpSent) {
      setError('No OTP request in progress. Please request OTP first.');
      return;
    }
    if (!otp || otp.trim().length === 0) {
      setError('Please enter the OTP');
      return;
    }

    setError('');
    setOtpVerifying(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/verify-otp`, {
        phone: devoteeInfo.phone,
        otp: otp.trim(),
      });
      if (response.data && response.data.success) {
        setOtpVerified(true);
        await checkDevotee(devoteeInfo.phone);
        setCurrentPage(3);
      } else {
        throw new Error(response.data?.error || 'OTP verification failed');
      }
    } catch (err) {
      console.error('OTP verification failed:', err);
      setError(err.response?.data?.error || 'OTP verification failed. Please check the code and try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleSkipOtp = async () => {
    if (!isLocalTesting) return;
    const phone = String(devoteeInfo.phone || '').trim();
    if (!/^[0-9]{10}$/.test(phone)) {
      setError('Enter a valid 10-digit phone number before skipping OTP');
      return;
    }

    setError('');
    setOtpSent(true);
    setOtpVerified(true);
    setLocalOtpMessage('OTP skipped for local testing.');
    await checkDevotee(phone);
    setCurrentPage(3);
  };

  // Save devotee information
  const saveDevotee = async () => {
    try {
      const devoteeData = {
        entity_id: entityId,
        event_id: eventId,
        name: devoteeInfo.name,
        phone: devoteeInfo.phone,
        address: devoteeInfo.address,
        pincode: devoteeInfo.pincode,
        city: devoteeInfo.city,
        district: devoteeInfo.district,
        state: devoteeInfo.state,
        email: devoteeInfo.email || ''
      };

      if (devoteeId) {
        devoteeData.devote_id = devoteeId;
      }

      const response = await axios.post(`${API_BASE_URL}/devotee/save`, devoteeData);
      
      if (response.data.success) {
        setDevoteeId(response.data.devote_id);
        console.log('Devotee saved with ID:', response.data.devote_id);
        return response.data.devote_id;
      }
    } catch (error) {
      console.error('Error saving devotee:', error);
      throw error;
    }
  };

        const fetchBlockerDatesDetails = async () => {
    const parseDateString = (dateString) => {
      if (!dateString || typeof dateString !== 'string') return null;
      const [year, month, day] = dateString.split('-').map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    };

    try {
      const response = await axios.get(`${API_BASE_URL}/blockerlist/${entityId}/${eventId}`);
      const allBlockers = Array.isArray(response.data) ? response.data : [];

      const globalBlocked = allBlockers.filter(item => Number(item.seva_code) === 0 && Number(item.no_of_sevas) === 0);
      const sevaSpecificBlocked = allBlockers.filter(item => Number(item.seva_code) === Number(sevaId) && Number(item.no_of_sevas) === 0);
      const filteredData = [...globalBlocked, ...sevaSpecificBlocked];

      const filterByNoOfSevas = allBlockers.filter(item => Number(item.seva_code) === Number(sevaId) && Number(item.no_of_sevas) > 0);
      setNoOfSevas(filterByNoOfSevas);

      const datesToBlock = filteredData
        .map(item => parseDateString(item.entry_date))
        .filter(Boolean);

      setBlockedDates(datesToBlock);
    } catch (error) {
      console.error("Error fetching blocked dates:", error);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    if (!entityId || !eventId || !sevaId) {
      setError('Missing required parameters: entity_id, event_id, seva_id');
      setLoading(false);
      return;
    }
    
    fetchData();
    fetchBlockerDatesDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, eventId, sevaId]);

  // Update document title when entity is loaded
  useEffect(() => {
    if (entity?.name) {
      document.title = `${entity.name} - Seva Booking`;
    } else {
      document.title = 'Shiroor Matha - Seva Booking';
    }
  }, [entity]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Initialize address table (first time setup) - optional. Controlled by env flag.
      // Set REACT_APP_INIT_ADDRESS_TABLE=true only for first-time deployments if you want the server to create the table.
      if (process.env.REACT_APP_INIT_ADDRESS_TABLE === 'true') {
        try {
          await axios.post(`${API_BASE_URL}/create-address-table`);
          console.log('Address table initialized successfully');
        } catch (tableError) {
          console.log('Address table setup skipped (optional):', tableError.message);
          // Don't show error to user, just continue
        }
      }
      
      // Fetch entity details
      const entityResponse = await axios.get(`${API_BASE_URL}/entity/${entityId}`);
  setEntity(entityResponse.data);
  console.log('Entity response:', entityResponse.data);
      // Determine if online payment is enabled for this entity (entity.online_payment expected 0/1)
      try {
        const op = Number(entityResponse.data?.online_payment);
        setOnlinePaymentEnabled(op === 1);
      } catch (e) {
        setOnlinePaymentEnabled(false);
      }
      
      // Fetch event details
      const eventResponse = await axios.get(`${API_BASE_URL}/event/${entityId}/${eventId}`);
      setEvent(eventResponse.data);
      
      // Fetch seva details
      try {
        const sevaResponse = await axios.get(`${API_BASE_URL}/seva/${entityId}/${eventId}/${sevaId}`);
        setSeva(sevaResponse.data);
        
        // Set minimum amount based on seva amount or 100
        const minAmount = Math.max(100, parseFloat(sevaResponse.data.seva_amount) || 100);
        setAmount(minAmount);
      } catch (sevaError) {
        // If seva not found, try to get all sevas for this event
        try {
          const allSevasResponse = await axios.get(`${API_BASE_URL}/sevas/${entityId}/${eventId}`);
          if (allSevasResponse.data.length === 0) {
            setError(`No sevas are currently available for this event. 
            
Available options:
• Entity ID 2, Event ID 1 (Shree Sukrathindra Swamy) - Has 8 active sevas
            
Please check with the organization or try a different entity/event combination.`);
          } else {
            setError(`Seva ID ${sevaId} not found for this event. Available sevas: ${allSevasResponse.data.map(s => `${s.seva_code}: ${s.seva_desc}`).join(', ')}`);
          }
        } catch (allSevasError) {
          setError(`Seva ID ${sevaId} not found. Please check the seva_id parameter in the URL.`);
        }
        return;
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        setError('Entity or Event not found. Please check the entity_id and event_id parameters.');
      } else {
        setError('Failed to load booking details. Please check the provided IDs.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPincodeDetails = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) return;
    
    try {
      setPincodeLoading(true);
      const response = await axios.get(`${API_BASE_URL}/pincode/${pincode}`);
      
      if (response.data.success) {
        setDevoteeInfo(prev => ({
          ...prev,
          city: String(response.data.data.city || ''),
          district: String(response.data.data.district || ''),
          state: String(response.data.data.state || '')
        }));
      }
    } catch (err) {
      console.log('Pincode lookup failed:', err.message);
      // Don't show error to user, just continue
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleDevoteeInfoChange = (field, value) => {
    setDevoteeInfo(prev => ({ ...prev, [field]: String(value || '') }));
    
    if (field === 'pincode') {
      if (value.length === 6) {
        fetchPincodeDetails(value);
      } else {
        // Clear city, district, state if pincode is incomplete
        setDevoteeInfo(prev => ({
          ...prev,
          city: '',
          district: '',
          state: ''
        }));
      }
    }
  };

  // Handle phone number changes with devotee lookup
  const handlePhoneChange = (value) => {
    setDevoteeInfo(prev => ({ ...prev, phone: String(value || '') }));
    setOtpSent(false);
    setOtpVerified(false);
    setOtp('');
    setError('');
  };

  const handlePhoneBlur = () => {
    if (devoteeInfo.phone && devoteeInfo.phone.length >= 10) {
      checkDevotee(devoteeInfo.phone);
    }
  };

  const validatePage1 = () => {
    if (!seva || !amount || parseFloat(amount) < 100 || !sevaDate) {
      setError('Please verify all details and ensure amount is at least ₹100 and a seva date is selected.');
      return false;
    }
    return true;
  };

  const validatePage2 = () => {
    const { name, phone, address, pincode, email } = devoteeInfo;
    if (!String(name || '').trim() || !String(phone || '').trim() || !String(address || '').trim() || !String(pincode || '').trim() || !String(email || '').trim()) {
      setError('Please fill all required fields (marked with *)');
      return false;
    }
    
    if (!/^\d{10}$/.test(phone)) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    
    if (!/^\d{6}$/.test(pincode)) {
      setError('Please enter a valid 6-digit pincode');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!prasadaCollectionMethod) {
      setError('Please select a prasada collection option');
      return false;
    }
    
    return true;
  };

  const handleProceedToPage2 = () => {
    if (validatePage1()) {
      setError('');
      setCurrentPage(2);
    }
  };

  // const handleBackToPage1 = () => {
  //   setCurrentPage(1);
  //   setError('');
  // };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handleCCAvenuePayment = async () => {
      try {
        setPaymentLoading(true);
        setError('');

        // 1. Save devotee information first (create or update)
        let finalDevoteeId = devoteeId;
        try {
          finalDevoteeId = await saveDevotee();
        } catch (devoteeError) {
          console.error('Error saving devotee:', devoteeError);
          setError('Failed to save devotee information. Please try again.');
          setPaymentLoading(false);
          return;
        }

        // 2. Get Encrypted Request from Backend
        const bookingData = {
            entity_id: entityId,
            event_id: eventId,
            seva_code: sevaId,
            name: devoteeInfo.name,
            phone: devoteeInfo.phone,
            address: devoteeInfo.address,
            city: devoteeInfo.city,
            district: devoteeInfo.district,
            state: devoteeInfo.state,
            pincode: devoteeInfo.pincode,
            email: devoteeInfo.email,
            amount: amount,
            seva_amount: seva?.seva_amount || amount,
            seva_date : formatLocalDate(sevaDate),
            devote_id: finalDevoteeId,
            remarks: `Prasada Collection: ${prasadaCollectionMethod === 'post' ? 'Via Post' : 'In Person'}`
        };
        
        // Use the current window location as the base for redirects, or the backend's known URL if handled there.
        // We need to tell the backend where to redirect AFTER CCAvenue posts back to the backend.
        // But CCAvenue redirects to the backend URL we configured/sent.
        // The backend then redirects to Frontend.
        // So we send the backend's own Callback URL to CCAvenue?
        // Actually, the `redirect_url` param sent to CCAvenue MUST be the backend endpoint `/api/ccav-response`.
        // The frontend doesn't need to send its own URL unless we want the backend to know where to go finally,
        // but I hardcoded `https://shiroor-matha-sevas.vercel.app` in the backend for now.
        
        const response = await axios.post(`${API_BASE_URL}/ccav-request`, {
            amount: amount,
            currency: 'INR',
            order_id: `ORDER_${entityId}_${eventId}_${Date.now()}`,
            redirect_url: `https://api.shiroormatha.org/api/ccav-response`,
            cancel_url: `https://api.shiroormatha.org/api/ccav-response`,
            booking_details: bookingData
        });

        if (response.data.success) {
            const { encRequest, access_code, action } = response.data;
            
            // 3. Create Form and Submit
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = action;
            form.style.display = 'none'; // hidden

            const inputEnc = document.createElement('input');
            inputEnc.type = 'hidden';
            inputEnc.name = 'encRequest';
            inputEnc.value = encRequest;
            form.appendChild(inputEnc);

            const inputAccess = document.createElement('input');
            inputAccess.type = 'hidden';
            inputAccess.name = 'access_code';
            inputAccess.value = access_code;
            form.appendChild(inputAccess);

            document.body.appendChild(form);
            form.submit();
            // User will be redirected.
        } else {
            setError('Failed to initiate payment: ' + response.data.error);
            setPaymentLoading(false);
        }

      } catch (err) {
          console.error('CCAvenue Payment Error:', err);
          setError('Payment initialization failed. Please try again.');
          setPaymentLoading(false);
      }
  };

  const handlePayment = async () => {
    if (!validatePage2()) {
      return;
    }

    if (paymentMethod === 'ccavenue') {
        await handleCCAvenuePayment();
        return;
    }

    try {
      setPaymentLoading(true);
      setError('');

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create order
      const orderResponse = await axios.post(`${API_BASE_URL}/create-order`, {
        amount: amount,
        currency: 'INR',
        receipt: `receipt_${entityId}_${eventId}_${Date.now()}`,
        entity_id: entityId
      });

      if (!orderResponse.data.success) {
        throw new Error('Failed to create payment order');
      }

      const order = orderResponse.data.order;

      // Razorpay options
      const options = {
        key: 'rzp_test_RDpSjpgML768P0', // Your Razorpay key ID from env
        amount: order.amount,
        currency: order.currency,
        name: entity?.name || 'Seva Booking',
        description: `${seva?.seva_desc} - ${event?.name}`,
        order_id: order.id,
        handler: async function (response) {
          try {
            // Save devotee information first (create or update)
            let finalDevoteeId = devoteeId;
            try {
              finalDevoteeId = await saveDevotee();
            } catch (devoteeError) {
              console.error('Error saving devotee:', devoteeError);
              // Continue with payment verification even if devotee save fails
            }

            // Verify payment
            const verifyResponse = await axios.post(`${API_BASE_URL}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              devote_id: finalDevoteeId, // Include devotee ID
              booking_details: {
                entity_id: entityId,
                event_id: eventId,
                seva_code: sevaId,
                name: devoteeInfo.name,
                phone: devoteeInfo.phone,
                address: devoteeInfo.address,
                city: devoteeInfo.city,
                district: devoteeInfo.district,
                state: devoteeInfo.state,
                pincode: devoteeInfo.pincode,
                amount: amount,
                seva_amount: seva?.seva_amount || amount,
                seva_date : formatLocalDate(sevaDate),
                seva_qty: 1,
                remarks: `Prasada Collection: ${prasadaCollectionMethod === 'post' ? 'Via Post' : 'In Person'}`
          }});

            console.log(verifyResponse)

            if (verifyResponse.data.success) {
              // Navigate to success page
              navigate(`/success?receipt=${verifyResponse.data.receipt_number}&payment_id=${verifyResponse.data.payment_id}&seva_name=${encodeURIComponent(seva?.seva_desc || '')}&remarks=${encodeURIComponent(verifyResponse.data.devotee_details?.remarks || '')}`);
            } else {
              throw new Error('Payment verification failed: ' + verifyResponse.data.message);
            }
          } catch (error) {
            console.error('Payment verification error:', error.response.data.message);
            navigate('/failure?reason=verification_failed&message=' + encodeURIComponent(error.response?.data?.message || error.message));
          }
        },
        prefill: {
          name: devoteeInfo.name,
          contact: devoteeInfo.phone,
        },
        theme: {
          color: '#FF6B35'
        },
        modal: {
          ondismiss: function() {
            navigate('/failure?reason=payment_cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment initiation error:', error);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="booking-container">
        <div className={`loading ${entity?.entity_id === 3 ? 'logo-loader' : ''}`}>
          <div className="loading-status">
            <div className="loading-spinner" />
            <span>Loading booking details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !entity) {
    return (
      <div className="booking-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      <div className="booking-card">
        {/* OTP overlay loader */}
        {(otpSending || otpVerifying) && (
          <div className={`loading overlay ${entity?.entity_id === 3 ? 'logo-loader' : ''}`}>
            <div className="loading-status">
              <div className="loading-spinner" />
              <span>{otpSending ? 'Sending OTP...' : 'Verifying OTP...'}</span>
            </div>
          </div>
        )}
        {/* Progress Indicator */}
        <div className="progress-indicator">
          <div className={`step ${currentPage >= 1 ? 'active' : ''}`}>
            <span>1</span>
            <label>Seva Details</label>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${currentPage >= 2 ? 'active' : ''}`}>
            <span>2</span>
            <label>Phone Verification</label>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${currentPage >= 3 ? 'active' : ''}`}>
            <span>3</span>
            <label>Personal Information</label>
          </div>
        </div>

        {/* Entity Logo and Header */}
        <div className="entity-header">
          <div className="entity-logo">
            <div className="logo-placeholder">
              {/* If backend provides a logo URL, show the image; otherwise show initial */}
              {entity?.logo_url || entity?.image_url ? (
                <img
                  src={entity.logo_url || entity.image_url}
                  alt={entity?.name || 'Entity logo'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                />
              ) : (
                (entity?.name?.charAt(0) || 'E')
              )}
            </div>
          </div>
          <div className="entity-info">
            <h1>{entity?.name}</h1>
            {entity?.name2 && <p>{entity?.name2}</p>}
            {entity?.name3 && <p>{entity?.name3}</p>}
          </div>
        </div>

        {/* Page 1: Seva Details */}
        {currentPage === 1 && (
          <div className="page-content">
            <h2>Seva Details</h2>
            
            {/* Event and Seva Details */}
            <div className="booking-details">
              {/* <div className="detail-section">
                <h3>Event Details</h3>
                <p><strong>Event:</strong> {event?.name}</p>
                <p><strong>Location:</strong> {event?.location}</p>
                <p><strong>Duration:</strong> {new Date(event?.start_date).toLocaleDateString()} - {new Date(event?.end_date).toLocaleDateString()}</p>
              </div> */}

              <div className="detail-section">
                <h3>Seva Details</h3>
                <p><strong>Seva:</strong> {seva?.seva_desc}</p>
                <p><strong>Amount:</strong> ₹{seva?.seva_amount}</p>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="sevadate">Seva Date *</label>
             <DatePicker
                id="sevadate"
                selected={sevaDate}
                onChange={(date) => {
                  if (!date) return;

                  const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  const selectedDateString = formatLocalDate(selectedDate);

                  const selectedDateObj = NoOfsevas.find(item => item.entry_date === selectedDateString);
                  if (selectedDateObj) {
                    if (Number(selectedDateObj.seva_limit) === 0) {
                      setError(`Only ${selectedDateObj.seva_limit} sevas are available on this date. Please choose another date or contact the organisation.`);
                      return;
                    }
                    setError('');
                  }

                  setSevaDate(selectedDate);
                }}
                minDate={new Date()}
                excludeDates={blockedDates}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                required
                maxDate={addMonths(new Date(), 3)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="amount">Seva Amount (₹) *</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => {
                  if (amountLocked) return;
                  setAmount(Math.max(100, parseInt(e.target.value) || 100));
                }}
                min="100"
                readOnly={amountLocked}
                required
              />
              <small>{amountLocked ? 'Amount is fixed for this seva.' : 'Minimum amount: ₹100'}</small>
              <small style={{ marginTop: '2px', color: '#DC143C' }}>Donors making donations are not eligible for tax exemption under Section 80G.</small>
            </div>

            {error && <div className="error-message">{error}</div>}

            {!onlinePaymentEnabled && onlinePaymentEnabled !== null ? (
              <div className="error-message">Online payments are not enabled for this entity. Please contact the organisation.</div>
            ) : (
              <button 
                className="proceed-button"
                onClick={handleProceedToPage2}
                disabled={onlinePaymentEnabled === false}
              >
                Proceed to Personal Details →
              </button>
            )}
          </div>
        )}

        {/* Page 2: Phone + OTP Verification */}
        {currentPage === 2 && (
          <div className="page-content">
            <h2>Phone Verification</h2>

            <p>To protect privacy, please verify the phone number with an OTP before viewing or editing details.</p>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="tel"
                  id="phone"
                  value={devoteeInfo.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="Enter your 10-digit phone number"
                  maxLength="10"
                  required
                />
                {otpSending && (
                  <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                    <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                  </div>
                )}
              </div>
            </div>

            <div id="recaptcha-container"></div>

            <div className="form-actions">
              <button
                className="back-button"
                onClick={() => { setCurrentPage(1); setError(''); }}
              >
                ← Back
              </button>

              <button
                className="proceed-button"
                onClick={handleSendOtp}
                disabled={otpSending}
              >
                {otpSending ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </div>

            {isLocalTesting && (
              <div style={{ marginTop: '12px', color: '#1f2937', fontSize: '14px' }}>
                <p style={{ margin: 0 }}><strong>Local test mode:</strong> OTP verification can be skipped for localhost.</p>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleSkipOtp}
                  style={{ marginTop: '8px' }}
                >
                  Skip OTP (Local Testing)
                </button>
              </div>
            )}

            {localOtpMessage && (
              <div style={{ marginTop: '12px', color: '#065f46', background: '#ecfdf5', padding: '12px', borderRadius: '12px' }}>
                {localOtpMessage}
              </div>
            )}

            {/* OTP entry shown after OTP sent */}
            {otpSent && !otpVerified && (
              <div style={{ marginTop: '16px' }}>
                <div className="form-group">
                  <label htmlFor="otp">Enter OTP</label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter the OTP sent to your phone"
                    maxLength="6"
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="back-button"
                    onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
                  >
                    ← Cancel
                  </button>

                  <button
                    className="proceed-button"
                    onClick={handleConfirmOtp}
                  >
                    Verify OTP
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Page 3: Personal Information */}
        {currentPage === 3 && (
          <div className="page-content">
            <h2>Personal Information</h2>
            
            <div className="seva-summary">
              <h4>Seva Summary:</h4>
              <p>{seva?.seva_desc} - ₹{amount}</p>
            </div>

            <div className="devotee-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  value={devoteeInfo.name}
                  onChange={(e) => handleDevoteeInfoChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="tel"
                    id="phone"
                    value={devoteeInfo.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onBlur={handlePhoneBlur}
                    placeholder="Enter your 10-digit phone number"
                    maxLength="10"
                    required
                    readOnly={!otpVerified} // make it read-only until OTP verified
                  />
                  {devoteeLoading && (
                    <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                      <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                    </div>
                  )}
                </div>
                {devoteeId && (
                  <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px' }}>
                    ✓ Existing devotee found - information pre-filled
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="address">Address *</label>
                <textarea
                  id="address"
                  value={devoteeInfo.address}
                  onChange={(e) => handleDevoteeInfoChange('address', e.target.value)}
                  placeholder="Enter your complete address"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={devoteeInfo.email}
                  onChange={(e) => handleDevoteeInfoChange('email', e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="prasadaCollectionMethod">Prasada Collection Method *</label>
                <select
                  id="prasadaCollectionMethod"
                  value={prasadaCollectionMethod}
                  onChange={(e) => setPrasadaCollectionMethod(e.target.value)}
                  required
                >
                  <option value="">Select an option</option>
                  <option value="in_person">Collect in person</option>
                  <option value="post">Via post</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pincode">Pincode *</label>
                <input
                  type="text"
                  id="pincode"
                  value={devoteeInfo.pincode}
                  onChange={(e) => handleDevoteeInfoChange('pincode', e.target.value)}
                  placeholder="Enter 6-digit pincode"
                  maxLength="6"
                  required
                />
                {pincodeLoading && <small>Looking up pincode...</small>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    value={devoteeInfo.city}
                    onChange={(e) => handleDevoteeInfoChange('city', e.target.value)}
                    placeholder="City (auto-filled from pincode)"
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="district">District</label>
                  <input
                    type="text"
                    id="district"
                    value={devoteeInfo.district}
                    onChange={(e) => handleDevoteeInfoChange('district', e.target.value)}
                    placeholder="District (auto-filled from pincode)"
                    readOnly
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  value={devoteeInfo.state}
                  onChange={(e) => handleDevoteeInfoChange('state', e.target.value)}
                  placeholder="State (auto-filled from pincode)"
                  readOnly
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

              <div className="form-actions">
              <button 
                className="back-button"
                onClick={() => { setCurrentPage(2); setError(''); }}
              >
                ← Back
              </button>
              
              <button 
                className="pay-button"
                onClick={handlePayment}
                disabled={paymentLoading || onlinePaymentEnabled === false}
              >
                {paymentLoading ? (
                  <>
                    <span className="button-spinner" />
                    Processing...
                  </>
                ) : `Pay ₹${amount}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingForm;
